/**
 * VisitorsTab — the "Plan a Visit" queue for an assembly.
 *
 * Someone said they're coming; this is where the welcome team picks it up:
 * reach out, mark them visited, and see instantly when a visitor went on to
 * apply for membership (the backend links the two by email).
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  CalendarHeart, Loader2, Mail, Phone, Search, MessageSquare, Trash2, UserCheck, Link2,
  Send, MailCheck, MailWarning, Settings2, UtensilsCrossed, Sparkles, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';
import EventSignups from './EventSignups';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (res.status === 204) return {};
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Request failed (${res.status})`);
  return data;
};

const STATUSES = [
  { key: 'new', label: 'New' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'visited', label: 'Visited' },
  { key: 'joined', label: 'Joined' },
  { key: 'archived', label: 'Archived' },
];

const TONE = {
  new: 'bg-sky-100 text-sky-700',
  contacted: 'bg-amber-100 text-amber-800',
  visited: 'bg-violet-100 text-violet-700',
  joined: 'bg-emerald-100 text-emerald-700',
  archived: 'bg-slate-100 text-slate-600',
};

const WaitingBanner = ({ churchId }) => {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['visitors-waiting', churchId],
    queryFn: () => api(`/api/admin/churches/${churchId}/visitors/waiting`),
  });

  const nudge = useMutation({
    mutationFn: () => api(`/api/admin/churches/${churchId}/visitors/nudge-now`,
      { method: 'POST' }),
    onSuccess: (r) => {
      if (r.sent_to?.length) toast.success(`Nudge sent to ${r.sent_to.join(', ')}`);
      else toast.error(`Not sent — ${(r.errors || [])[0] || r.skipped || 'no recipients'}`);
      qc.invalidateQueries({ queryKey: ['visitors-waiting', churchId] });
      qc.invalidateQueries({ queryKey: ['church-visitors', churchId] });
    },
    onError: (e) => toast.error(e.message),
  });

  const overdue = data?.overdue || [];
  if (!data || overdue.length === 0) return null;

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex flex-wrap items-center gap-3"
         data-testid="visitors-waiting-banner">
      <MailWarning className="h-4 w-4 text-amber-700 shrink-0" />
      <p className="text-sm text-amber-900">
        <strong>{overdue.length}</strong> {overdue.length === 1 ? 'person has' : 'people have'} been
        waiting more than {data.nudge_after_days} day
        {data.nudge_after_days === 1 ? '' : 's'} for a welcome
        {overdue[0] ? ` — ${overdue[0].full_name} for ${overdue[0].days_waiting} day(s)` : ''}.
      </p>
      <Button size="sm" variant="outline" className="h-7 text-xs ml-auto"
              data-testid="visitors-nudge-now" disabled={nudge.isPending}
              onClick={() => nudge.mutate()}>
        <Send className="h-3 w-3 mr-1" />
        {nudge.isPending ? 'Sending…' : 'Nudge the team now'}
      </Button>
    </div>
  );
};

const FollowupBanner = ({ churchId }) => {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['visitor-followups', churchId],
    queryFn: () => api(`/api/admin/churches/${churchId}/visitors/followups`),
  });

  const sendAll = useMutation({
    mutationFn: () => api(`/api/admin/churches/${churchId}/visitors/send-followups`,
      { method: 'POST' }),
    onSuccess: (r) => {
      if (r.sent) toast.success(`${r.sent} lunch invite(s) sent`);
      else toast.error(`Nothing sent — ${(r.failed || [])[0]?.reason || r.skipped || 'nobody due'}`);
      qc.invalidateQueries({ queryKey: ['visitor-followups', churchId] });
      qc.invalidateQueries({ queryKey: ['church-visitors', churchId] });
    },
    onError: (e) => toast.error(e.message),
  });

  const due = data?.due || [];
  if (!data || due.length === 0) return null;

  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 flex flex-wrap items-center gap-3"
         data-testid="visitor-followups-banner">
      <UtensilsCrossed className="h-4 w-4 text-indigo-700 shrink-0" />
      <p className="text-sm text-indigo-900">
        <strong>{due.length}</strong> {due.length === 1 ? 'person is' : 'people are'} due
        the newcomers' lunch invite
        {data.enabled ? '' : ' (the automatic invite is switched off)'}.
      </p>
      <Button size="sm" variant="outline" className="h-7 text-xs ml-auto"
              data-testid="visitor-followups-send-all" disabled={sendAll.isPending}
              onClick={() => sendAll.mutate()}>
        <Send className="h-3 w-3 mr-1" />
        {sendAll.isPending ? 'Sending…' : 'Send the invites now'}
      </Button>
    </div>
  );
};

const exportChannels = (data) => {
  const rows = [
    ...(data.source_trends?.window || []).map((s) => ({ period: `last ${data.months} months`, ...s })),
    ...(data.source_trends?.all_time || []).map((s) => ({ period: 'all time', ...s })),
  ];
  if (!rows.length) return;
  const cols = ['period', 'label', 'count', 'visited', 'joined', 'join_rate', 'share'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [cols.join(','), ...rows.map((r) => cols.map((c) => esc(r[c])).join(','))].join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  a.download = 'visitor-channels.csv';
  a.click();
  URL.revokeObjectURL(a.href);
};

const Insights = ({ churchId }) => {
  const { data } = useQuery({
    queryKey: ['visitor-insights', churchId],
    queryFn: () => api(`/api/admin/churches/${churchId}/visitors/insights?months=6`),
  });
  if (!data) return null;
  const t = data.totals;
  const peak = Math.max(1, ...data.series.map((s) => s.new));
  const cells = [
    ['New visitors', t.new, 'text-sky-700'],
    ['Came along', t.visited, 'text-violet-700'],
    ['Returning faces', t.returning, 'text-amber-700'],
    ['Joined as members', t.joined, 'text-emerald-700'],
    ['Join rate', `${t.join_rate}%`, 'text-slate-800'],
  ];
  return (
    <Card data-testid="visitor-insights">
      <CardContent className="p-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {cells.map(([label, value, tone]) => (
            <div key={label} className="text-center">
              <p className={`text-xl font-bold ${tone}`}
                 data-testid={`insight-${label.split(' ')[0].toLowerCase()}`}>{value}</p>
              <p className="text-[11px] text-slate-500">{label}</p>
            </div>
          ))}
        </div>
        <div className="flex items-end gap-2 h-24" data-testid="visitor-insights-chart">
          {data.series.map((s) => (
            <div key={s.month} className="flex-1 flex flex-col items-center gap-1"
                 title={`${s.month}: ${s.new} new · ${s.visited} came · ${s.returning} returning · ${s.joined} joined`}>
              <div className="w-full flex flex-col justify-end h-16">
                <div className="w-full bg-emerald-500 rounded-t"
                     style={{ height: `${(s.joined / peak) * 100}%` }} />
                <div className="w-full bg-sky-400"
                     style={{ height: `${((s.new - s.joined) / peak) * 100}%` }} />
              </div>
              <span className="text-[10px] text-slate-400">{s.month.slice(5)}</span>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-slate-400">
          Last 6 months · {data.all_time} visitor{data.all_time === 1 ? '' : 's'} all time.
          Blue is everyone who signed up, green is those who joined.
        </p>
        {(data.sources || []).length > 0 && (
          <div className="border-t border-slate-100 pt-3 space-y-3" data-testid="visitor-sources">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                Which channels actually bring members
              </p>
              {data.source_trends?.best_label && (
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]"
                       data-testid="visitor-source-best">
                  <Sparkles className="h-3 w-3 mr-1" />
                  Best converting: {data.source_trends.best_label}
                </Badge>
              )}
              <button onClick={() => exportChannels(data)}
                      data-testid="visitor-sources-export"
                      className="ml-auto text-[11px] text-indigo-600 hover:underline">
                <Download className="h-3 w-3 inline mr-0.5" /> Export CSV
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[['window', `Last ${data.months} months`], ['all_time', 'All time']].map(
                ([bucket, heading]) => (
                <div key={bucket} className="space-y-1.5"
                     data-testid={`visitor-sources-${bucket.replace('_', '-')}`}>
                  <p className="text-[11px] font-semibold text-slate-400">{heading}</p>
                  {((data.source_trends?.[bucket]) || []).length === 0 ? (
                    <p className="text-xs text-slate-400">Nothing yet.</p>
                  ) : data.source_trends[bucket].map((s) => (
                    <div key={s.key} data-testid={`visitor-source-${bucket}-${s.key}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-600 flex-1 truncate">{s.label}</span>
                        <span className="text-[11px] text-slate-500">
                          {s.count} → {s.visited} → <strong className="text-emerald-700">{s.joined}</strong>
                        </span>
                        <span className="text-[11px] w-12 text-right font-semibold text-slate-700">
                          {s.join_rate}%
                        </span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-0.5">
                        <div className={`h-full rounded-full ${s.joined
                          ? 'bg-emerald-500' : 'bg-slate-300'}`}
                             style={{ width: `${Math.max(s.join_rate, s.joined ? 4 : 2)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-400">
              Read each row as signed up → came along → joined, and the percentage is how many
              of that channel's visitors became members.
              {data.source_trends?.no_joins?.length
                ? ` No members yet from ${data.source_trends.no_joins.join(', ')}.`
                : ''}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const WelcomeSettingsDialog = ({ churchId, open, onOpenChange }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState(null);

  const { data } = useQuery({
    queryKey: ['welcome-settings', churchId],
    queryFn: () => api(`/api/admin/churches/${churchId}/welcome-settings`),
    enabled: open,
  });

  const current = form || data;
  const set = (k) => (e) => setForm({ ...(current || {}),
    [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const sourceText = ((current?.sources) || [])
    .map((s) => (typeof s === 'string' ? s : s.label)).join('\n');

  const save = useMutation({
    mutationFn: () => api(`/api/admin/churches/${churchId}/welcome-settings`,
      { method: 'PUT', body: JSON.stringify(current) }),
    onSuccess: () => {
      toast.success('Welcome note saved — new visitors get this straight away');
      qc.invalidateQueries({ queryKey: ['welcome-settings', churchId] });
      setForm(null);
      onOpenChange(false);
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setForm(null); onOpenChange(v); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto"
                     data-testid="welcome-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-4 w-4 text-indigo-600" /> Welcome email
          </DialogTitle>
        </DialogHeader>
        {!current ? <Loader2 className="h-5 w-5 animate-spin mx-auto my-6" /> : (
          <div className="space-y-3 text-sm">
            <label className="flex items-center gap-2 font-medium">
              <input type="checkbox" checked={!!current.auto_send}
                     data-testid="welcome-auto-send" onChange={set('auto_send')} />
              Email every new visitor the moment they sign up
            </label>
            <div className="space-y-1.5">
              <span className="text-slate-600">Service times</span>
              <Textarea rows={2} value={current.service_times || ''}
                        onChange={set('service_times')} data-testid="welcome-service-times"
                        placeholder={'Sunday 9:00am & 11:00am\nWednesday 6:30pm — Bible study'} />
            </div>
            <div className="space-y-1.5">
              <span className="text-slate-600">Directions & what to expect</span>
              <Textarea rows={3} value={current.directions || ''}
                        onChange={set('directions')} data-testid="welcome-directions"
                        placeholder="Where to park, which entrance, how long the service runs…" />
            </div>
            <div className="space-y-1.5">
              <span className="text-slate-600">A personal line from the team</span>
              <Textarea rows={2} value={current.welcome_note || ''}
                        onChange={set('welcome_note')} data-testid="welcome-note"
                        placeholder="Come as you are — we'll save you a seat." />
            </div>
            <div className="space-y-1.5">
              <span className="text-slate-600">Follow-up nudge</span>
              <div className="flex items-center gap-2">
                <input type="number" min="0" max="60"
                       value={current.nudge_after_days ?? 5}
                       onChange={set('nudge_after_days')}
                       data-testid="welcome-nudge-days"
                       className="h-9 w-20 rounded-md border border-slate-200 px-2 text-sm" />
                <span className="text-xs text-slate-500">
                  days — then email the team about anyone still in "New". 0 turns it off.
                </span>
              </div>
            </div>
            <p className="text-[11px] text-slate-500">
              Your address and phone number are pulled from the church's public page
              automatically, so you don't have to repeat them here.
            </p>

            <div className="border-t border-slate-100 pt-3 space-y-3">
              <label className="flex items-center gap-2 font-medium">
                <input type="checkbox" checked={current.followup_enabled !== false}
                       data-testid="followup-enabled" onChange={set('followup_enabled')} />
                Invite them to the newcomers' lunch a week later
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <input type="number" min="1" max="60"
                       value={current.followup_after_days ?? 7}
                       onChange={set('followup_after_days')}
                       data-testid="followup-days"
                       className="h-9 w-20 rounded-md border border-slate-200 px-2 text-sm" />
                <span className="text-xs text-slate-500">days after</span>
                <select value={current.followup_anchor || 'welcome_email'}
                        onChange={set('followup_anchor')} data-testid="followup-anchor"
                        className="h-9 rounded-md border border-slate-200 px-2 text-sm">
                  <option value="welcome_email">the welcome email</option>
                  <option value="service_date">their service date</option>
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <span className="text-slate-600">Lunch date & time</span>
                  <Input value={current.lunch_when || ''} onChange={set('lunch_when')}
                         data-testid="followup-lunch-when"
                         placeholder="Sunday 12 July, 12:30pm" />
                </div>
                <div className="space-y-1.5">
                  <span className="text-slate-600">Where</span>
                  <Input value={current.lunch_venue || ''} onChange={set('lunch_venue')}
                         data-testid="followup-lunch-venue"
                         placeholder="The church hall, side entrance" />
                </div>
              </div>
              <div className="space-y-1.5">
                <span className="text-slate-600">A line to add to the invite</span>
                <Textarea rows={2} value={current.followup_note || ''}
                          onChange={set('followup_note')} data-testid="followup-note"
                          placeholder="Bring the children — there's plenty for them to do." />
              </div>
              <div className="space-y-1.5">
                <span className="text-slate-600">"How did you hear about us?" options</span>
                <Textarea rows={4} data-testid="followup-sources"
                          value={sourceText}
                          onChange={(e) => setForm({ ...(current || {}),
                            sources: e.target.value.split('\n').map((l) => ({ label: l }))
                              .filter((s) => s.label.trim()) })}
                          placeholder={'A friend or family member\nSocial media\nGoogle / web search'} />
                <p className="text-[11px] text-slate-500">
                  One per line — this is the list new visitors pick from. Leave it as is
                  unless you want your own wording.
                </p>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={!current || save.isPending}
                  data-testid="welcome-settings-save">
            {save.isPending ? 'Saving…' : 'Save welcome email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function VisitorsTab({ churchId }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState('new');
  const [search, setSearch] = useState('');
  const [noting, setNoting] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [welcomeOpen, setWelcomeOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['church-visitors', churchId, status, search],
    queryFn: () => api(`/api/admin/churches/${churchId}/visitors?status=${status}`
      + (search ? `&search=${encodeURIComponent(search)}` : '')),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['church-visitors', churchId] });

  const update = useMutation({
    mutationFn: ({ id, patch }) => api(`/api/admin/churches/${churchId}/visitors/${id}`,
      { method: 'PATCH', body: JSON.stringify(patch) }),
    onSuccess: () => { toast.success('Visitor updated'); invalidate(); setNoting(null); },
    onError: (e) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (id) => api(`/api/admin/churches/${churchId}/visitors/${id}`,
      { method: 'DELETE' }),
    onSuccess: () => { toast.success('Visitor removed'); invalidate(); },
    onError: (e) => toast.error(e.message),
  });

  const resend = useMutation({
    mutationFn: (id) => api(
      `/api/admin/churches/${churchId}/visitors/${id}/resend-welcome`, { method: 'POST' }),
    onSuccess: (r) => {
      if (r.sent) toast.success('Welcome email sent');
      else toast.error(`Not sent — ${r.reason || 'unknown reason'}`);
      invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const followup = useMutation({
    mutationFn: (id) => api(
      `/api/admin/churches/${churchId}/visitors/${id}/send-followup`, { method: 'POST' }),
    onSuccess: (r) => {
      if (r.sent) toast.success('Newcomers\' lunch invite sent');
      else toast.error(`Not sent — ${r.reason || 'unknown reason'}`);
      invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const counts = data?.counts || {};
  const items = data?.items || [];

  return (
    <div className="space-y-4" data-testid="visitors-tab">
      <WaitingBanner churchId={churchId} />
      <FollowupBanner churchId={churchId} />
      <Insights churchId={churchId} />
      <EventSignups churchId={churchId} />

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-md border border-slate-200 overflow-hidden bg-white">
          {STATUSES.map((s) => (
            <button
              key={s.key} onClick={() => setStatus(s.key)}
              data-testid={`visitors-filter-${s.key}`}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                status === s.key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              {s.label}{counts[s.key] ? ` (${counts[s.key]})` : ''}
            </button>
          ))}
        </div>
        <div className="relative max-w-xs flex-1 min-w-0 sm:min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search name, email or phone..." value={search}
                 onChange={(e) => setSearch(e.target.value)} className="pl-10"
                 data-testid="visitors-search" />
        </div>
        <span className="text-xs text-slate-500 ml-auto" data-testid="visitors-total">
          {data?.total || 0} visitor{(data?.total || 0) === 1 ? '' : 's'} in total
        </span>
        <Button size="sm" variant="outline" className="h-8 text-xs"
                data-testid="welcome-settings-open" onClick={() => setWelcomeOpen(true)}>
          <Settings2 className="h-3.5 w-3.5 mr-1" /> Welcome email
        </Button>
      </div>

      {isLoading ? (
        <Loader2 className="h-6 w-6 animate-spin mx-auto my-10" />
      ) : items.length === 0 ? (
        <Card><CardContent className="py-14 text-center" data-testid="visitors-empty">
          <CalendarHeart className="h-8 w-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-600">No {status} visitors.</p>
          <p className="text-xs text-slate-500 mt-1">
            Visitors land here when someone uses "Plan a Visit" on your public page.
          </p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {items.map((v) => (
            <Card key={v.id} data-testid={`visitor-card-${v.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-900">{v.full_name}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                      <CalendarHeart className="h-3 w-3" />
                      Coming {v.preferred_service_date || 'soon'}
                    </p>
                    {v.heard_about && (
                      <p className="text-[11px] text-slate-400 mt-0.5"
                         data-testid={`visitor-heard-${v.id}`}>
                        Heard about you via {v.heard_about.replace(/_/g, ' ')}
                      </p>
                    )}
                  </div>
                  <Badge className={`${TONE[v.status] || TONE.new} hover:opacity-100 capitalize`}
                         data-testid={`visitor-status-${v.id}`}>{v.status}</Badge>
                </div>

                <div className="flex flex-col gap-0.5">
                  {v.email && (
                    <a href={`mailto:${v.email}`}
                       className="text-xs text-indigo-600 hover:underline flex items-center gap-1 truncate">
                      <Mail className="h-3 w-3" /> {v.email}
                    </a>
                  )}
                  {v.phone && (
                    <a href={`tel:${v.phone}`}
                       className="text-xs text-slate-600 hover:underline flex items-center gap-1">
                      <Phone className="h-3 w-3" /> {v.phone}
                    </a>
                  )}
                </div>

                {v.message && (
                  <p className="text-xs text-slate-600 bg-slate-50 rounded-md px-3 py-2 whitespace-pre-line">
                    {v.message}
                  </p>
                )}
                {v.notes && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
                     data-testid={`visitor-notes-${v.id}`}>
                    <MessageSquare className="h-3 w-3 inline mr-1" />{v.notes}
                  </p>
                )}
                {v.welcome_email && (
                  <div className="flex items-center gap-2 flex-wrap"
                       data-testid={`visitor-welcome-${v.id}`}>
                    {v.welcome_email.sent ? (
                      <Badge variant="outline"
                             className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                        <MailCheck className="h-3 w-3 mr-1" /> Welcome email sent
                      </Badge>
                    ) : (
                      <Badge variant="outline"
                             className="text-[10px] bg-rose-50 text-rose-700 border-rose-200">
                        <MailWarning className="h-3 w-3 mr-1" />
                        Not sent — {v.welcome_email.reason || 'unknown'}
                      </Badge>
                    )}
                    <button onClick={() => resend.mutate(v.id)} disabled={resend.isPending}
                            data-testid={`visitor-resend-${v.id}`}
                            className="text-[11px] text-indigo-600 hover:underline">
                      <Send className="h-3 w-3 inline mr-0.5" /> Resend
                    </button>
                  </div>
                )}
                {v.followup_email && (
                  <Badge variant="outline"
                         className={`text-[10px] ${v.followup_email.sent
                           ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                           : 'bg-rose-50 text-rose-700 border-rose-200'}`}
                         data-testid={`visitor-followup-${v.id}`}>
                    <UtensilsCrossed className="h-3 w-3 mr-1" />
                    {v.followup_email.sent
                      ? 'Lunch invite sent'
                      : `Lunch invite not sent — ${v.followup_email.reason || 'unknown'}`}
                  </Badge>
                )}
                {v.application_id && (
                  <Badge variant="outline"
                         className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200"
                         data-testid={`visitor-linked-${v.id}`}>
                    <Link2 className="h-3 w-3 mr-1" />
                    {v.member_id ? 'Became a member' : 'Membership application on file'}
                  </Badge>
                )}

                <div className="flex flex-wrap gap-1.5 pt-1">
                  {STATUSES.filter((s) => s.key !== v.status && s.key !== 'joined').map((s) => (
                    <Button key={s.key} size="sm" variant="outline" className="h-7 text-xs"
                            data-testid={`visitor-set-${s.key}-${v.id}`}
                            disabled={update.isPending}
                            onClick={() => update.mutate({ id: v.id, patch: { status: s.key } })}>
                      <UserCheck className="h-3 w-3 mr-1" /> {s.label}
                    </Button>
                  ))}
                  <Button size="sm" variant="ghost" className="h-7 text-xs"
                          data-testid={`visitor-send-followup-${v.id}`}
                          disabled={followup.isPending}
                          onClick={() => followup.mutate(v.id)}>
                    <UtensilsCrossed className="h-3 w-3 mr-1" /> Lunch invite
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs"
                          data-testid={`visitor-note-${v.id}`}
                          onClick={() => { setNoting(v); setNoteText(v.notes || ''); }}>
                    <MessageSquare className="h-3 w-3 mr-1" /> Note
                  </Button>
                  <Button size="sm" variant="ghost"
                          className="h-7 text-xs text-rose-600 hover:text-rose-700"
                          data-testid={`visitor-delete-${v.id}`}
                          onClick={() => {
                            if (window.confirm(`Remove ${v.full_name} from the visitor list?`)) {
                              remove.mutate(v.id);
                            }
                          }}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <WelcomeSettingsDialog churchId={churchId} open={welcomeOpen}
                             onOpenChange={setWelcomeOpen} />

      <Dialog open={!!noting} onOpenChange={(v) => !v && setNoting(null)}>
        <DialogContent data-testid="visitor-note-dialog">
          <DialogHeader><DialogTitle>Note on {noting?.full_name}</DialogTitle></DialogHeader>
          <Textarea rows={4} value={noteText} onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Who called them, what was agreed…"
                    data-testid="visitor-note-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoting(null)}>Cancel</Button>
            <Button data-testid="visitor-note-save" disabled={update.isPending}
                    onClick={() => update.mutate({ id: noting.id, patch: { notes: noteText } })}>
              Save note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
